// src/auth/dev-auto-login.ts
/**
 * LOCAL-DEV-ONLY auto-login helper (mirrors the PM app's dev-auto-login).
 *
 * Purpose: let an engineer (or an automated agent driving a browser) reach the
 * authenticated Practitioner app on a local `vite dev` server without typing
 * credentials, and without an existing "already logged in" 409 blocking the flow.
 * Pure developer convenience — it MUST NEVER activate in any deployed build.
 *
 * Production safety: gated on `import.meta.env.DEV`, which Vite sets to `true`
 * ONLY when running the local dev server. Every `vite build` output —
 * production AND the deployed dev site — has `import.meta.env.DEV === false`,
 * so this branch is dead-stripped from the shipped bundle and can never be
 * enabled by a misconfigured env var or URL.
 *
 * Credentials come ONLY from the environment: `VITE_DEV_LOGIN_EMAIL` /
 * `VITE_DEV_LOGIN_PASSWORD` (or `VITE_AUTH_USERNAME` / `VITE_AUTH_PASSWORD`),
 * set in a git-ignored `.env.local`. There is NO literal fallback. This file
 * used to carry the shared dev provider account's password as a default, which
 * put a live credential in the repository (SEC-1). When no credentials are
 * configured, auto-login FAILS CLOSED: nothing is attempted and the normal
 * login page shows. `src/auth/__tests__/dev-auto-login.test.ts` keeps it so.
 */

/**
 * Auto-login is enabled ONLY on a local Vite dev server. Any built/deployed
 * bundle leaves it false — the normal manual login form is shown and an
 * active-session 409 keeps its existing (SkipConflict) handling.
 */
export const DEV_AUTO_LOGIN_ENABLED = import.meta.env.DEV;

export interface DevLoginCredentials {
  username: string;
  password: string;
}

/** Dev test-user credentials from the environment. Empty when not configured. */
export const DEV_AUTO_LOGIN_CREDENTIALS: DevLoginCredentials = {
  username: import.meta.env.VITE_DEV_LOGIN_EMAIL ?? '',
  password: import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? '',
};

/**
 * The credentials to auto-sign-in with, or `null` when none are configured.
 * Resolves WHOLE PAIRS, never single fields: the `primary` pair (the
 * `VITE_AUTH_*` bring-up pair) when both its username and password are set,
 * else the `fallback` pair (`VITE_DEV_LOGIN_*`) when both of its are set.
 * Mixing one source's username with another's password would sign in with a
 * mismatched pair. With no complete pair the caller must not attempt a login.
 */
export function resolveDevAutoLoginCredentials(
  primary: Partial<DevLoginCredentials>,
  fallback: Partial<DevLoginCredentials> = DEV_AUTO_LOGIN_CREDENTIALS,
): DevLoginCredentials | null {
  for (const pair of [primary, fallback]) {
    if (pair.username && pair.password) {
      return { username: pair.username, password: pair.password };
    }
  }
  return null;
}
